import { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { submitFeedback, todaysSessionId } from './feedbackApi';
import type { FeedbackCategory } from './types';

const CATEGORIES: Array<{ key: FeedbackCategory; label: string }> = [
  { key: 'confusing', label: 'Something is confusing' },
  { key: 'improve', label: 'Suggestion to improve the course' },
  { key: 'praise', label: 'Something is working well' },
  { key: 'tech', label: 'A tech problem' },
  { key: 'other', label: 'Something else' },
];

const BODY_MAX = 4000;

/** The always-on feedback box. Reads its own state — takes no props. */
export default function FeedbackBox() {
  const [category, setCategory] = useState<FeedbackCategory>('confusing');
  const [body, setBody] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [aboutToday, setAboutToday] = useState(false);
  const [todaySession, setTodaySession] = useState<{ id: string; label: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    todaysSessionId().then(setTodaySession);
  }, []);

  const submit = async () => {
    if (!body.trim()) {
      setError('Write a sentence or two first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        category,
        body: body.trim(),
        is_anonymous: anonymous,
        class_session_id: aboutToday && todaySession ? todaySession.id : undefined,
      });
      setSent(true);
      setBody('');
    } catch {
      setError('Could not send your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-ws-ink">Tell the instructor</h3>
      <p className="mt-0.5 text-xs text-ws-ink-3">
        A private note to the instructor. Nothing here is quoted or shared without your separate, explicit consent.
      </p>

      <div className="mt-3 space-y-3">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
          className="w-full rounded-lg border border-ws-border bg-ws-surface p-2 text-sm text-ws-ink outline-none focus:border-ws-accent"
        >
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>

        <div>
          <textarea
            rows={3}
            maxLength={BODY_MAX}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What's on your mind?"
            className="w-full rounded-lg border border-ws-border bg-ws-surface p-2.5 text-sm text-ws-ink outline-none focus:border-ws-accent"
          />
          <div className="mt-0.5 text-right text-xs text-ws-ink-4">{body.length}/{BODY_MAX}</div>
        </div>

        <label className="flex items-start gap-2 text-xs text-ws-ink-2">
          <input type="checkbox" className="mt-0.5" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
          <span>
            Send anonymously
            {anonymous
              ? ' — the instructor will see this with no name attached.'
              : " — the instructor will see this came from you."}
          </span>
        </label>

        {todaySession && (
          <label className="flex items-start gap-2 text-xs text-ws-ink-2">
            <input type="checkbox" className="mt-0.5" checked={aboutToday} onChange={(e) => setAboutToday(e.target.checked)} />
            <span>This is about today's class ({todaySession.label}).</span>
          </label>
        )}

        {error && <p className="text-xs text-ws-danger">{error}</p>}
        {sent && <p className="text-xs text-ws-success">Sent. Thank you.</p>}

        <div className="flex justify-end">
          <Button size="sm" loading={submitting} onClick={submit}>Send</Button>
        </div>
      </div>
    </Card>
  );
}
